class VlBrandStrip extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.vl-brandstrip-section');
    this.list = this.querySelector('[data-vl-brandstrip-list]');
    this.track = this.querySelector('[data-vl-brandstrip-track]');
    this.prevBtn = this.querySelector('[data-vl-brandstrip-prev]');
    this.nextBtn = this.querySelector('[data-vl-brandstrip-next]');
    this.marquee = this.dataset.marquee === 'true';

    if (!this.list) return;

    if (this.marquee) {
      this.setupMarquee();
    } else {
      this.setupScroll();
    }

    // Auto-fit logos that overflow their tiles (especially on narrow mobile viewports).
    // Runs after layout is settled (rAF), again after web fonts load, and on resize.
    this._scheduleFit();
    this._resizeHandler = () => this._scheduleFit();
    window.addEventListener('resize', this._resizeHandler, { passive: true });
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this._scheduleFit()).catch(() => {});
    }
  }

  disconnectedCallback() {
    this.stopAutoplay();
    if (this._resizeHandler) window.removeEventListener('resize', this._resizeHandler);
    if (this._fitRaf) cancelAnimationFrame(this._fitRaf);
    if (this._fitTimeout) clearTimeout(this._fitTimeout);
  }

  _scheduleFit() {
    // Debounce + rAF so we run once per layout pass even with rapid resize events.
    if (this._fitTimeout) clearTimeout(this._fitTimeout);
    this._fitTimeout = setTimeout(() => {
      if (this._fitRaf) cancelAnimationFrame(this._fitRaf);
      this._fitRaf = requestAnimationFrame(() => this.fitLogos());
    }, 60);
  }

  fitLogos() {
    const items = this.querySelectorAll('.vl-brandstrip__item');
    items.forEach(item => {
      // Available inner width = tile - horizontal padding (and tiny safety margin)
      const cs = getComputedStyle(item);
      const padX = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight);
      const innerWidth = item.clientWidth - padX - 2;
      if (innerWidth <= 0) return;

      // Text-based logo: shrink font-size until it fits
      const textLogo = item.querySelector('.vl-brandstrip__logo');
      if (textLogo) {
        // Reset any previous shrink so we can re-measure cleanly on resize
        const stored = textLogo.dataset.vlOriginalFontSize;
        if (stored) {
          textLogo.style.fontSize = stored;
        } else {
          textLogo.dataset.vlOriginalFontSize = textLogo.style.fontSize || getComputedStyle(textLogo).fontSize;
        }

        let size = parseFloat(getComputedStyle(textLogo).fontSize);
        const minSize = 8;
        let guard = 40;
        while (textLogo.scrollWidth > innerWidth && size > minSize && guard > 0) {
          size -= 0.5;
          textLogo.style.fontSize = size + 'px';
          guard--;
        }
        // Tighten letter-spacing too if still too tight
        if (textLogo.scrollWidth > innerWidth) {
          textLogo.style.letterSpacing = '0';
        }
      }

      // Image logo: enforce strict containment (CSS handles most, this is a guard)
      const imgLogo = item.querySelector('.vl-brandstrip__logo-img');
      if (imgLogo) {
        imgLogo.style.maxWidth = '100%';
        imgLogo.style.objectFit = 'contain';
      }
    });
  }

  setupMarquee() {
    if (!this.section) return;
    this.prevBtn?.addEventListener('click', () => {
      this.section.style.setProperty('--vl-bs-marquee-dir', 'reverse');
    });
    this.nextBtn?.addEventListener('click', () => {
      this.section.style.setProperty('--vl-bs-marquee-dir', 'normal');
    });
    this.prevBtn?.removeAttribute('disabled');
    this.nextBtn?.removeAttribute('disabled');
  }

  setupScroll() {
    this.prevBtn?.addEventListener('click', () => this.scrollByItems(-1));
    this.nextBtn?.addEventListener('click', () => this.scrollByItems(1));
    this.list.addEventListener('scroll', () => this.updateNav(), { passive: true });

    this.updateNav();
    this.startAutoplay();
    this.list.addEventListener('mouseenter', () => this.stopAutoplay());
    this.list.addEventListener('mouseleave', () => this.startAutoplay());
    this.list.addEventListener('focusin', () => this.stopAutoplay());
  }

  itemWidth() {
    const first = this.list.querySelector('.vl-brandstrip__item');
    if (!first) return 0;
    const gap = 8;
    return first.getBoundingClientRect().width + gap;
  }

  scrollByItems(direction) {
    const step = this.itemWidth();
    if (!step) return;
    this.list.scrollBy({ left: step * direction, behavior: 'smooth' });
  }

  updateNav() {
    if (!this.prevBtn || !this.nextBtn) return;
    const max = this.list.scrollWidth - this.list.clientWidth - 1;
    this.prevBtn.toggleAttribute('disabled', this.list.scrollLeft <= 0);
    this.nextBtn.toggleAttribute('disabled', this.list.scrollLeft >= max);
  }

  startAutoplay() {
    const speed = parseInt(this.getAttribute('autoplay-speed') || '0', 10);
    if (!speed) return;
    this.stopAutoplay();
    this.timer = setInterval(() => {
      const max = this.list.scrollWidth - this.list.clientWidth - 1;
      if (this.list.scrollLeft >= max) {
        this.list.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        this.scrollByItems(1);
      }
    }, speed * 1000);
  }

  stopAutoplay() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }
}

if (!customElements.get('vl-brand-strip')) {
  customElements.define('vl-brand-strip', VlBrandStrip);
}
